import { trackEventView } from "../../hooks/useAnalyticsTracking";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase, AssociationActivityRow } from "../../lib/supabase";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import DOMPurify from "dompurify";
import SEO from "@/components/SEO";
import { ArticleSchema, BreadcrumbSchema } from "@/components/StructuredData";

const CATEGORY_LABELS: Record<string, { vi: string; color: string }> = {
  news: { vi: "Tin tức", color: "bg-[#004A99]/10 text-[#004A99]" },
  event: { vi: "Sự kiện", color: "bg-amber-100 text-amber-700" },
  cooperation: { vi: "Hợp tác", color: "bg-green-100 text-green-700" },
  seminar: { vi: "Hội thảo", color: "bg-violet-100 text-violet-700" },
  mou: { vi: "MOU / Ký kết", color: "bg-rose-100 text-rose-700" },
  exhibition: { vi: "Triển lãm", color: "bg-teal-100 text-teal-700" },
  other: { vi: "Khác", color: "bg-gray-100 text-gray-600" },
};

const AssociationDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [activity, setActivity] = useState<AssociationActivityRow | null>(null);
  const [related, setRelated] = useState<AssociationActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const fetch = async () => {
      const { data } = await supabase.from("association_activities").select("*").eq("id", id).maybeSingle();
      setActivity(data as AssociationActivityRow ?? null);

      if (data) {
        const { data: rel } = await supabase
          .from("association_activities")
          .select("id, title, image_url, category, published_at")
          .eq("is_published", true)
          .neq("id", id)
          .order("published_at", { ascending: false })
          .limit(4);
        setRelated((rel as AssociationActivityRow[]) ?? []);
        trackEventView(data.id, "association_activity", data.title ?? "", window.location.pathname);
      }
      setLoading(false);
    };
    fetch();
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <i className="ri-loader-4-line animate-spin text-3xl text-[#004A99]" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-32">
          <i className="ri-error-warning-line text-4xl text-gray-300 mb-4" aria-hidden="true" />
          <p className="text-gray-500 mb-6">Không tìm thấy bài viết này</p>
          <Link to="/hoat-dong-hiep-hoi-va-su-kien" className="px-5 py-2 bg-[#004A99] text-white text-sm rounded-xl cursor-pointer whitespace-nowrap">Quay lại danh sách</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const catInfo = CATEGORY_LABELS[activity.category ?? ""] ?? CATEGORY_LABELS.other;
  const gallery = activity.gallery ?? [];
  const formattedDate = activity.published_at ? new Date(activity.published_at).toLocaleDateString("vi-VN", { year: "numeric", month: "long", day: "numeric" }) : "";
  const siteUrl = typeof window !== "undefined" ? `${window.location.protocol}//${window.location.host}` : "";

  // Auto-linkify plain URLs that are not already inside <a> tags
  const autoLinkContent = (html: string): string => {
    const parts = html.split(/(<[^>]+>)/g);
    return parts.map((part, i) => {
      if (i % 2 === 1) return part; // it's a tag, skip
      return part.replace(
        /(https?:\/\/[^\s<>"']+)/g,
        '<a href="$1" target="_blank" rel="nofollow noopener noreferrer">$1</a>'
      );
    }).join("");
  };

  const sanitizedContent = activity.content
    ? DOMPurify.sanitize(autoLinkContent(activity.content), {
        ADD_ATTR: ["target", "rel"],
        ALLOWED_TAGS: ["p","br","strong","b","em","i","u","s","h1","h2","h3","h4","h5","h6","ul","ol","li","blockquote","a","img","table","thead","tbody","tr","th","td","hr","span","div","pre","code"],
        ALLOWED_ATTR: ["href","src","alt","title","target","rel","class","style","width","height"],
      })
    : "";

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${activity.title} | Hoạt Động Hiệp Hội KCANIA`}
        description={activity.subtitle || `${activity.title}. Tin tức và hoạt động của Hiệp Hội Carbon Nano Hàn–Việt KCANIA.`}
        keywords={`${activity.title}, hoạt động hiệp hội, KCANIA, Carbon Nano, Hàn Quốc Việt Nam${activity.tags ? `, ${activity.tags.join(", ")}` : ""}`}
        ogImage={activity.image_url || undefined}
        ogType="article"
        articlePublishedTime={activity.published_at || undefined}
        articleAuthor={activity.author || "KCANIA Editorial"}
        articleSection={catInfo.vi}
        articleTags={activity.tags || []}
      />
      <ArticleSchema
        headline={activity.title}
        description={activity.subtitle || undefined}
        image={activity.image_url || undefined}
        datePublished={activity.published_at || undefined}
        author={activity.author || undefined}
        url={window.location.href}
        keywords={activity.tags || []}
      />
      <BreadcrumbSchema
        items={[
          { name: "Trang chủ", url: siteUrl || "/" },
          { name: "Hoạt Động Hiệp Hội", url: `${siteUrl}/hoat-dong-hiep-hoi-va-su-kien` },
          { name: activity.title, url: window.location.href },
        ]}
      />

      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center cursor-pointer"
          onClick={() => setLightboxSrc(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Xem ảnh phóng to"
        >
          <img src={lightboxSrc} alt="" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" />
          <button className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white cursor-pointer" aria-label="Đóng">
            <i className="ri-close-line text-xl" aria-hidden="true" />
          </button>
        </div>
      )}

      <Navbar />

      {/* Cover image — mobile: auto height to show full image; desktop: fixed height */}
      {activity.image_url && (
        <div className="w-full relative overflow-hidden" style={{ minHeight: "200px" }}>
          <img
            src={activity.image_url}
            alt={activity.title}
            className="w-full h-auto md:h-[500px] object-contain md:object-cover object-top block"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" aria-hidden="true" />
        </div>
      )}

      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-10">
          {/* Main */}
          <article className="flex-1 min-w-0" itemScope itemType="https://schema.org/Article">
            <meta itemProp="datePublished" content={activity.published_at || ""} />
            <meta itemProp="author" content={activity.author || "KCANIA"} />

            {/* Breadcrumb — single line, small font */}
            <nav aria-label="Đường dẫn điều hướng" className="flex items-center gap-1 mb-5 flex-wrap" style={{ fontSize: "12px", color: "#9ca3af" }}>
              <Link to="/" className="hover:text-[#004A99] cursor-pointer whitespace-nowrap">Trang chủ</Link>
              <i className="ri-arrow-right-s-line text-xs" aria-hidden="true" />
              <Link to="/hoat-dong-hiep-hoi-va-su-kien" className="hover:text-[#004A99] cursor-pointer whitespace-nowrap">Hoạt Động Hiệp Hội</Link>
              <i className="ri-arrow-right-s-line text-xs" aria-hidden="true" />
              <span className="text-gray-600 truncate max-w-[160px] md:max-w-none">{activity.title}</span>
            </nav>

            {/* Header */}
            <header className="mb-8 w-full">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold whitespace-nowrap ${catInfo.color}`}>{catInfo.vi}</span>
                {activity.is_featured && (
                  <span className="text-xs px-2.5 py-1 rounded-full font-semibold bg-amber-100 text-amber-700 whitespace-nowrap">Nổi bật</span>
                )}
              </div>
              <h1 className="text-xl md:text-3xl font-black text-gray-900 leading-tight mb-4 w-full" itemProp="headline">{activity.title}</h1>
              {activity.subtitle && <p className="text-lg text-gray-500 leading-relaxed mb-4" itemProp="description">{activity.subtitle}</p>}
              <div className="flex items-center gap-4 text-sm text-gray-400 flex-wrap">
                {formattedDate && (
                  <time className="flex items-center gap-1.5 whitespace-nowrap" dateTime={activity.published_at || ""} itemProp="datePublished">
                    <i className="ri-calendar-line text-[#004A99]" aria-hidden="true" />
                    {formattedDate}
                  </time>
                )}
                {activity.event_date && (
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <i className="ri-time-line text-[#004A99]" aria-hidden="true" />
                    {activity.event_date}
                  </span>
                )}
                {activity.event_location && (
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <i className="ri-map-pin-line text-[#004A99]" aria-hidden="true" />
                    {activity.event_location}
                  </span>
                )}
                {activity.author && (
                  <span className="flex items-center gap-1.5 whitespace-nowrap" itemProp="author">
                    <i className="ri-user-line text-[#004A99]" aria-hidden="true" />
                    {activity.author}
                  </span>
                )}
              </div>
            </header>

            {/* Content */}
            {sanitizedContent && (
              <>
                <style>{`
                  .assoc-content a { color: #0066cc; text-decoration: underline; }
                  .assoc-content a:hover { color: #004A99; font-weight: 600; }
                  .assoc-content h1,.assoc-content h2,.assoc-content h3 { font-weight: 800; margin: 1.2em 0 0.5em; color: #212529; }
                  .assoc-content h1 { font-size: 1.5rem; }
                  .assoc-content h2 { font-size: 1.25rem; }
                  .assoc-content h3 { font-size: 1.1rem; }
                  .assoc-content p { margin-bottom: 0.9em; line-height: 1.75; }
                  .assoc-content ul,.assoc-content ol { padding-left: 1.5em; margin-bottom: 0.9em; }
                  .assoc-content li { margin-bottom: 0.3em; }
                  .assoc-content blockquote { border-left: 3px solid #004A99; padding-left: 1em; color: #555; margin: 1em 0; font-style: italic; }
                  .assoc-content img { max-width: 100%; border-radius: 8px; margin: 0.8em 0; }
                  .assoc-content table { width: 100%; border-collapse: collapse; margin: 1em 0; }
                  .assoc-content th,.assoc-content td { border: 1px solid #e5e7eb; padding: 0.5em 0.75em; text-align: left; font-size: 0.875rem; }
                  .assoc-content th { background: #f3f4f6; font-weight: 700; }
                  .assoc-content hr { border: none; border-top: 1px solid #e5e7eb; margin: 1.5em 0; }
                `}</style>
                <div
                  className="assoc-content mb-10 text-gray-700 text-base"
                  dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                  itemProp="articleBody"
                />
              </>
            )}

            {/* Gallery */}
            {gallery.length > 0 && (
              <div className="mb-10">
                <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <i className="ri-image-2-line text-[#004A99]" aria-hidden="true" />
                  Thư Viện Ảnh
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {gallery.map((src, idx) => (
                    <div
                      key={idx}
                      className="aspect-video overflow-hidden rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setLightboxSrc(src)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && setLightboxSrc(src)}
                      aria-label={`Xem ảnh ${idx + 1}`}
                    >
                      <img src={src} alt={`${activity.title} – ảnh ${idx + 1}`} className="w-full h-full object-cover object-top" loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {activity.tags && activity.tags.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-2 flex-wrap">
                  <i className="ri-price-tag-3-line text-gray-400" aria-hidden="true" />
                  {activity.tags.map((tag) => (
                    <span key={tag} className="text-xs px-3 py-1 rounded-full bg-[#F8F9FA] text-gray-600 border border-gray-200 whitespace-nowrap">{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Link gốc */}
            {activity.link_url && (
              <div className="mb-8 p-4 bg-[#EDF2FB] rounded-xl border border-[#004A99]/15 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#004A99]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-external-link-line text-[#004A99]" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#004A99]">Link bài viết gốc</p>
                    <p className="text-xs text-[#004A99]/60 truncate max-w-[300px]">{activity.link_url}</p>
                  </div>
                </div>
                <a
                  href={activity.link_url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex-shrink-0 px-4 py-2 bg-[#004A99] text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap hover:bg-[#003d7d] transition-colors"
                >
                  Xem bài gốc
                </a>
              </div>
            )}

            {/* Nav */}
            <div className="border-t border-gray-100 pt-6 flex justify-between items-center">
              <Link to="/hoat-dong-hiep-hoi-va-su-kien" className="flex items-center gap-2 text-sm text-[#004A99] font-semibold hover:underline cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line" aria-hidden="true" />
                Quay lại danh sách
              </Link>
            </div>
          </article>

          {/* Sidebar — full width on mobile, fixed 256px on desktop */}
          <aside className="w-full lg:w-64 lg:flex-shrink-0 mt-2 lg:mt-0" aria-label="Thông tin sự kiện và bài viết liên quan">
            <div className="lg:sticky lg:top-[135px] space-y-4">
              {/* Event info */}
              {(activity.event_date || activity.event_location) && (
                <div className="bg-[#004A99] rounded-xl p-4 text-white" style={{ marginTop: "20px" }}>
                  <p className="text-xs font-bold mb-3 opacity-70 uppercase tracking-wider">Thông Tin Sự Kiện</p>
                  {activity.event_date && (
                    <div className="flex items-start gap-2 mb-2">
                      <i className="ri-calendar-event-line mt-0.5 opacity-70" aria-hidden="true" />
                      <div>
                        <p className="text-[10px] opacity-60 mb-0.5">Thời gian</p>
                        <p className="text-sm font-semibold">{activity.event_date}</p>
                      </div>
                    </div>
                  )}
                  {activity.event_location && (
                    <div className="flex items-start gap-2">
                      <i className="ri-map-pin-line mt-0.5 opacity-70" aria-hidden="true" />
                      <div>
                        <p className="text-[10px] opacity-60 mb-0.5">Địa điểm</p>
                        <p className="text-sm font-semibold">{activity.event_location}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Related */}
              {related.length > 0 && (
                <div className="bg-white border border-gray-100 rounded-xl p-4 w-full" style={{ marginTop: "20px" }}>
                  <h4 className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wider">Bài Viết Liên Quan</h4>
                  <div className="space-y-3">
                    {related.map((r) => (
                      <Link key={r.id} to={`/hoat-dong-hiep-hoi-va-su-kien/${r.id}`} className="flex items-start gap-3 group cursor-pointer">
                        {/* Fixed 60x60 thumbnail */}
                        <div className="flex-shrink-0 rounded-lg overflow-hidden bg-gray-100" style={{ width: "60px", height: "60px" }}>
                          {r.image_url ? (
                            <img src={r.image_url} alt={r.title} className="w-full h-full object-cover object-top" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-[#004A99]/10">
                              <i className="ri-newspaper-line text-[#004A99]/40" aria-hidden="true" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1" style={{ minWidth: 0 }}>
                          <p
                            className="font-medium text-gray-700 group-hover:text-[#004A99] transition-colors leading-snug"
                            style={{
                              fontSize: "14px",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {r.title}
                          </p>
                          <time className="text-[10px] text-gray-400 mt-1 whitespace-nowrap block" dateTime={r.published_at || ""}>
                            {r.published_at ? new Date(r.published_at).toLocaleDateString("vi-VN") : ""}
                          </time>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <Link to="/hoat-dong-hiep-hoi-va-su-kien" className="mt-3 block text-center text-xs text-[#004A99] font-semibold hover:underline cursor-pointer">
                    Xem tất cả →
                  </Link>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AssociationDetailPage;
